import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Input, Button, Card, Typography, Modal, notification, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import documentSequenceService from '../../../services/documentSequenceService';

const { Title, Text } = Typography;

const LABELS = { INVOICE: 'Factura', FITO: 'Fito', SALES_ORDER: 'Orden de venta', DISPATCH: 'Despacho' };

// Solo estos documentos imprimen una leyenda.
const LEGEND_DOCUMENTS = ['INVOICE', 'FITO'];
// Mismos topes del backend: lo que cabe en el pie del PDF sin tapar contenido.
const MAX_LEGEND_LENGTH = 500;
const MAX_LEGEND_LINES = 4;

// Misma regla que formatDocNumber del backend: prefijo tal cual (sin espacios
// alrededor, como lo guarda) y padding como mínimo, nunca recorta.
const formatDocNumber = (number, padding, prefix) =>
  `${(prefix || '').trim()}${String(number).padStart(padding, '0')}`;

const DocumentSequenceCard = ({ sequence, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const lastValue = Form.useWatch('last_value', form);
  const padding = Form.useWatch('padding', form);
  const prefix = Form.useWatch('prefix', form);

  useEffect(() => {
    form.setFieldsValue({
      prefix: sequence.prefix,
      last_value: sequence.last_value,
      padding: sequence.padding,
      legend: sequence.legend,
      declaration: sequence.declaration,
    });
  }, [form, sequence]);

  const save = async (values) => {
    setSaving(true);
    try {
      const response = await documentSequenceService.actualizarConsecutivo(sequence.document, values);
      onSaved(response.data);
      notification.success({ message: `Consecutivo de ${LABELS[sequence.document]} actualizado` });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.mensaje || 'No se pudo guardar el consecutivo.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();

    if (values.last_value === sequence.last_value) {
      return save(values);
    }

    // Subir el número no tiene vuelta atrás: se confirma antes de guardar.
    Modal.confirm({
      title: `¿Cambiar el número actual de ${LABELS[sequence.document]}?`,
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: `Pasará de ${sequence.last_value} a ${values.last_value}. Después no se podrá disminuir.`,
      okText: 'Cambiar',
      cancelText: 'Cancelar',
      okButtonProps: { style: { backgroundColor: '#1a3c2e' } },
      onOk: () => save(values),
    });
  };

  const preview = Number.isInteger(lastValue) && Number.isInteger(padding)
    ? formatDocNumber(lastValue + 1, padding, prefix)
    : '—';

  return (
    <Card title={LABELS[sequence.document]} style={{ width: 360 }}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="prefix"
          label="Prefijo"
          extra='Incluye el separador, por ejemplo "FAC-"'
          rules={[{
            // Misma regla del backend: los buscadores confundirían el dígito final con el número.
            validator: (_, value) => (value && /\d$/.test(value.trim())
              ? Promise.reject(new Error('El prefijo no puede terminar en un número'))
              : Promise.resolve()),
          }]}
        >
          <Input maxLength={10} placeholder="Sin prefijo" />
        </Form.Item>

        <Form.Item
          name="last_value"
          label="Número actual"
          extra={
            <Text type="warning">
              <ExclamationCircleOutlined /> Este valor no se puede disminuir
            </Text>
          }
          rules={[{ required: true, message: 'Campo requerido' }]}
        >
          <InputNumber min={sequence.last_value} precision={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="padding"
          label="Dígitos mínimos"
          extra="0 = sin ceros"
          rules={[{ required: true, message: 'Campo requerido' }]}
        >
          <InputNumber min={0} max={10} precision={0} style={{ width: '100%' }} />
        </Form.Item>

        {LEGEND_DOCUMENTS.includes(sequence.document) && (
          <Form.Item
            name="legend"
            label="Leyenda"
            rules={[{
              validator: (_, value) => (value && value.trim().split('\n').length > MAX_LEGEND_LINES
                ? Promise.reject(new Error(`La leyenda admite máximo ${MAX_LEGEND_LINES} líneas`))
                : Promise.resolve()),
            }]}
          >
            <Input.TextArea rows={4} maxLength={MAX_LEGEND_LENGTH} showCount />
          </Form.Item>
        )}

        {sequence.document === 'FITO' && (
          <Form.Item name="declaration" label="Declaración">
            <Input.TextArea rows={3} />
          </Form.Item>
        )}
      </Form>

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Próximo número: </Text>
        <Text strong>{preview}</Text>
      </div>

      <Button type="primary" onClick={handleSave} loading={saving} style={{ backgroundColor: '#1a3c2e' }}>
        Guardar
      </Button>
    </Card>
  );
};

const DocumentSequencesSection = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSequences = async () => {
      try {
        const response = await documentSequenceService.listarConsecutivos();
        setSequences(response.data);
      } catch {
        notification.error({ message: 'Error', description: 'No se pudieron cargar los consecutivos.' });
      } finally {
        setLoading(false);
      }
    };

    fetchSequences();
  }, []);

  const handleSaved = (updated) => {
    setSequences((current) => current.map((s) => (s.document === updated.document ? updated : s)));
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={4} style={{ color: '#595959', fontWeight: 600 }}>
        Consecutivos
      </Title>
      {/* Cada documento guarda su número formateado al crearse. */}
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Los cambios aplican solo a documentos nuevos.
      </Text>
      <Spin spinning={loading}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {Object.keys(LABELS)
            .map((document) => sequences.find((s) => s.document === document))
            .filter(Boolean)
            .map((sequence) => (
              <DocumentSequenceCard key={sequence.document} sequence={sequence} onSaved={handleSaved} />
            ))}
        </div>
      </Spin>
    </div>
  );
};

export default DocumentSequencesSection;
